/**
 * Charm builder cart: removing one bundle line removes the whole group.
 *
 * Strategy:
 *   - Capture-phase document click on remove link / qty-minus inside the cart drawer.
 *   - Read window.liquidAjaxCart.cart synchronously to decide if it's a bundle remove.
 *   - Scope removal STRICTLY to the bundle's `_Bundle Group` timestamp so multiple
 *     bundles with the same Bundle Title in the cart stay independent.
 *   - preventDefault + stopImmediatePropagation BEFORE Liquid Ajax Cart's bubble-phase
 *     listeners run, then remove each line via direct JSON fetch to /cart/change.js,
 *     re-reading /cart.js between calls so the line key is always current.
 *   - A single liquidAjaxCart.update at the end refreshes the cart drawer UI.
 *
 * Do NOT set requestState.info.cancel = true on request-start — the library still
 * dispatches request-end and its own handler crashes reading responseData.body
 * of null.
 */

const BUNDLE_GROUP_PROPS = ['_Bundle Group', '_Bundle Id', 'Bundle Id'];
const BUNDLE_TITLE_PROP = 'Bundle Title';

function getItemProperties(item) {
  const raw = item?.properties;
  if (!raw) return {};
  if (Array.isArray(raw)) {
    const out = {};
    raw.forEach((entry) => {
      const key = entry?.name ?? entry?.key;
      if (key != null && entry?.value != null) {
        out[String(key)] = entry.value;
      }
    });
    return out;
  }
  return typeof raw === 'object' ? raw : {};
}

function getBundleGroupFromItem(item) {
  const props = getItemProperties(item);
  for (const key of BUNDLE_GROUP_PROPS) {
    const val = props[key];
    if (val != null && String(val).trim() !== '') return String(val).trim();
  }
  return '';
}

function isBundleLine(item) {
  return Boolean(getBundleGroupFromItem(item));
}

function findBundleLines(cart, groupKey) {
  if (!groupKey) return [];
  return cart.items.filter((line) => getBundleGroupFromItem(line) === groupKey);
}

function getLineKeyFromHref(href, cart) {
  if (!href) return '';
  try {
    const url = new URL(href, window.location.origin);
    const id = url.searchParams.get('id');
    if (id && id.includes(':')) return id;
    const lineParam = url.searchParams.get('line');
    if (lineParam) {
      const idx = parseInt(lineParam, 10);
      if (idx >= 1 && idx <= cart.items.length) return cart.items[idx - 1].key;
    }
    if (id) {
      const asNumber = parseInt(id, 10);
      if (!Number.isNaN(asNumber)) {
        const byVariant = cart.items.find(
          (line) => line.variant_id === asNumber || line.id === asNumber
        );
        if (byVariant) return byVariant.key;
      }
    }
  } catch {
    /* malformed URL */
  }
  return '';
}

function resolveLineKey(target, cart) {
  const lineEl = target.closest('[data-line-key]');
  const fromAttr = lineEl?.getAttribute('data-line-key');
  if (fromAttr) return fromAttr;

  const button = target.closest('[data-ajax-cart-request-button]');
  if (button) return getLineKeyFromHref(button.getAttribute('href'), cart);

  return '';
}

function fetchCart() {
  const root = window.Shopify?.routes?.root || '/';
  return fetch(`${root}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((r) => {
    if (!r.ok) throw new Error('Cart fetch failed');
    return r.json();
  });
}

async function postCartChange(lineKey) {
  const root = window.Shopify?.routes?.root || '/';
  const res = await fetch(`${root}cart/change.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify({ id: lineKey, quantity: 0 }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.description || err?.message || 'Cart change failed');
  }
  return res.json();
}

function setBundleLinesLoading(lineKeys, isLoading) {
  lineKeys.forEach((key) => {
    document.querySelectorAll(`.hy-line[data-line-key="${key}"]`).forEach((el) => {
      if (isLoading) {
        el.setAttribute('data-loading', 'true');
        el.setAttribute('aria-busy', 'true');
      } else {
        el.removeAttribute('data-loading');
        el.removeAttribute('aria-busy');
      }
    });
  });
}

/** Re-resolve bundle lines after each removal so line keys are always current. */
async function removeWholeBundle(groupKey) {
  const MAX_ITERATIONS = 30;
  let removed = 0;

  for (let i = 0; i < MAX_ITERATIONS; i += 1) {
    const cart = await fetchCart();
    const bundleLines = findBundleLines(cart, groupKey);
    if (bundleLines.length === 0) break;

    // Remove from the end so other lines' positions don't shift mid-loop.
    const target = bundleLines[bundleLines.length - 1];
    if (!target?.key) break;

    try {
      await postCartChange(target.key);
      removed += 1;
    } catch (err) {
      console.warn('[bundle-builder-cart] remove failed', err);
      break;
    }
  }

  return removed;
}

function refreshCartUI() {
  if (typeof window.liquidAjaxCart?.update === 'function') {
    window.liquidAjaxCart.update({}, {});
  }
}

let bundleRemovalBusy = false;

function onCartClickCapture(event) {
  if (bundleRemovalBusy) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  const removeBtn = event.target.closest('[data-ajax-cart-request-button]');
  const minusBtn = event.target.closest('[data-ajax-cart-quantity-minus]');
  if (!removeBtn && !minusBtn) return;

  const cart = window.liquidAjaxCart?.cart;
  if (!cart?.items?.length) return;

  const lineKey = resolveLineKey(event.target, cart);
  if (!lineKey) return;

  const anchorItem = cart.items.find((line) => line.key === lineKey);
  if (!anchorItem || !isBundleLine(anchorItem)) return;

  if (minusBtn && anchorItem.quantity > 1) return;

  const groupKey = getBundleGroupFromItem(anchorItem);
  if (!groupKey) return;

  const bundleLines = findBundleLines(cart, groupKey);
  if (bundleLines.length <= 1) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  event.stopPropagation();

  bundleRemovalBusy = true;
  const lineKeys = bundleLines.map((line) => line.key);
  setBundleLinesLoading(lineKeys, true);

  removeWholeBundle(groupKey)
    .catch((err) => {
      console.warn('[bundle-builder-cart] bundle removal interrupted', err);
    })
    .finally(() => {
      bundleRemovalBusy = false;
      setBundleLinesLoading(lineKeys, false);
      refreshCartUI();
    });
}

if (!window.__bundleBuilderCartRemoveBound) {
  window.__bundleBuilderCartRemoveBound = true;
  document.addEventListener('click', onCartClickCapture, true);
}
